import { useEffect, useState } from 'react';
import type { Awareness } from 'y-protocols/awareness';
import { resolveUserColor } from '../../../engine/render/user-color';
// CanvasAwarenessState lives in the engine layer so render components can import it
// without crossing the engine→shell seam. Re-export for shell consumers.
import type { CanvasAwarenessState } from '../../../engine/render/awareness-types';
export type { CanvasAwarenessState } from '../../../engine/render/awareness-types';

export interface UseCanvasAwarenessArgs {
  awareness: Awareness;
  /** user.color is optional — if omitted, resolved deterministically from user.id via resolveUserColor. */
  user: { id: string; name: string; color?: string };
}

export interface UseCanvasAwarenessResult {
  remoteStates: Map<number, CanvasAwarenessState>;
}

export function useCanvasAwareness({
  awareness,
  user,
}: UseCanvasAwarenessArgs): UseCanvasAwarenessResult {
  const [remoteStates, setRemoteStates] = useState<Map<number, CanvasAwarenessState>>(new Map());

  // INIT: NEVER setLocalStateField — it's a silent no-op when localState is null.
  // Deps: depend on user PRIMITIVES (id/name/color) so an inline literal at the call site
  // doesn't cause re-init flicker on every render.
  // Resolve color from userId if not explicitly provided.
  const resolvedColor = user.color ?? resolveUserColor(user.id);
  useEffect(() => {
    const existing = awareness.getLocalState() ?? {};
    // Do NOT clobber a richer editor-set `user` (the editor writes a 7-key
    // shape: avatar/clientId/etc.). When the host already set `user`, preserve
    // it; otherwise seed the canvas's 3-key identity. Canvas readers only need
    // user.name + user.color, both present in either shape.
    const existingUser = (existing as Record<string, unknown>).user;
    // The canvas only OWNS `user` when no host (editor) set it first. When the
    // host owns it, we preserve the richer object and must NOT strip it on
    // cleanup. Track ownership so unmount removes only what the canvas added.
    const canvasOwnsUser = existingUser === undefined;
    const resolvedUser: CanvasAwarenessState['user'] = {
      id: user.id,
      name: user.name,
      color: resolvedColor,
    };
    awareness.setLocalState({
      ...existing,
      user: existingUser ?? resolvedUser,
      canvasCursor: null,
      canvasSelection: [],
      canvasEdgeSelection: [],
      canvasInFlightEdge: null,
    });
    return () => {
      // Strip canvas-owned (namespaced) fields. Strip `user` only when the
      // canvas seeded it — in an embedded canvas the host editor owns `user`.
      // Null the whole state only if nothing else remains.
      const current = awareness.getLocalState();
      if (!current) return;
      const {
        canvasCursor: _c,
        canvasSelection: _s,
        canvasEdgeSelection: _es,
        canvasInFlightEdge: _if,
        user: _u,
        ...withoutUser
      } = current as Record<string, unknown>;
      const rest = canvasOwnsUser ? withoutUser : { ...withoutUser, user: _u };
      if (Object.keys(rest).length === 0) {
        awareness.setLocalState(null);
      } else {
        awareness.setLocalState(rest);
      }
    };
  }, [awareness, user.id, user.name, resolvedColor]);

  useEffect(() => {
    const update = () => {
      const next = new Map<number, CanvasAwarenessState>();
      awareness.getStates().forEach((state, clientId) => {
        if (clientId === awareness.clientID) return;
        if (!state || !(state as Partial<CanvasAwarenessState>).user) return;
        next.set(clientId, state as CanvasAwarenessState);
      });
      setRemoteStates(next);
    };
    update();
    // Use 'update' — not 'change'.
    awareness.on('update', update);
    return () => {
      awareness.off('update', update);
    };
  }, [awareness]);

  return { remoteStates };
}
